import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { login, logout, refresh, userBalance } from 'rest/urls'
import HeadersManager from 'rest/HeadersManager'
import { DELETE, GET, POST } from 'rest/request'
import * as nativeApp from 'utils/nativeApp'
import { parseJwt } from 'utils/parseJwt'

export const AVAILABLE_LANGUAGES = {
  pl: 'pl',
  en: 'en'
}

export const fetchLogIn = createAsyncThunk('login/fetchLogIn', async body => {
  const response = await POST(login, { body, credentials: true })
  return response
})

export const fetchLogOut = createAsyncThunk('login/fetchLogOut', async () => {
  const response = await DELETE(logout)
  return response
})

export const fetchToken = createAsyncThunk('login/fetchToken', async () => {
  const response = await POST(refresh, { credentials: true }, false)
  return response
})

export const getBalance = createAsyncThunk('login/getBalance', async () => {
  const response = await GET(userBalance)
  return response
})

const getInitialState = (isLoading = false) => {
  return {
    isLoading,
    loggedIn: false,
    contract: false,
    expirationDate: 0,
    language: AVAILABLE_LANGUAGES.pl,
    notificationsEnabled: false,
    user: {
      name: '',
      pin: false,
      device_verification: false,
    }
  }
}

const logInUser = (state, data) => {
  if (data) {
    const { expires_in: expirationDate, cfg, access_token: token, hasDataForContract } = data
    const { name = '', sub = '' } = parseJwt(token)
    HeadersManager.jwt.set(token)
    state.expirationDate = (expirationDate - 60) * 1000
    state.user = { ...cfg, name, id: sub }
    state.notificationsEnabled = !!cfg.hasPushNotification
    state.language = cfg.language
    state.isLoading = false
    state.loggedIn = true
    state.error = ''
    if (hasDataForContract && hasDataForContract.fields) {
      state.contract = hasDataForContract.fields
    }

  } else {
    HeadersManager.jwt.destroy()
    return getInitialState()
  }
}

export const loginSlice = createSlice({
  name: 'login',
  initialState: getInitialState(true),
  reducers: {
    addPin: (state, _) => { state.user.pin = true },
    resetContract: (state, _) => { state.contract = false },
    setAppLoading: (state, action) => { state.isLoading = action.payload },
    updateLanguage: (state, action) => { state.language = action.payload },
    updateFavorites: (state, action) => { state.user.favorites = action.payload },
    updateShortcuts: (state, action) => { state.user.shortcuts = action.payload },
    updateUser: (state, action) => { state.user = { ...state.user, ...action.payload } },
    updateDeviceVerification: (state, action) => { state.user.device_verification = action.payload },
    updateNotification: (state, action) => { state.notificationsEnabled = action.payload },
  },
  extraReducers: {
    [fetchLogIn.fulfilled]: (state, action) => {
      const data = logInUser(state, action.payload)
      if (action.payload?.access_token) {
        nativeApp.registerPushNotifications(false)
      }
      if (data) return data
    },
    [fetchLogOut.fulfilled]: (state, _) => {
      if (state?.user?.hasPushNotification) {
        nativeApp.unregisterPushNotifications(false)
      }
      return logInUser(state, false)
    },

    [fetchToken.fulfilled]: (state, action) => {
      const data = logInUser(state, action.payload)
      nativeApp.checkPushNotificationStatus()
      if (data) return data
    },
    [fetchToken.pending]: (state, _) => { state.isLoading = true },
    [fetchToken.rejected]: (state, _) => {
      const data = logInUser(state, false)
      nativeApp.checkPushNotificationStatus()
      if (data) return data
    },

    [getBalance.fulfilled]: (state, action) => {
      const { invoices, unpaidInvoicesAmount } = action.payload
      state.user.unpaidInvoicesAmount = unpaidInvoicesAmount
      state.user.unpaid_invoices = invoices
    }
  },
})

export const { addPin, resetContract, updateLanguage, setAppLoading, updateFavorites, updateShortcuts, updateUser, updateDeviceVerification, updateNotification } = loginSlice.actions

export const selectExpirationDate = state => state.login.expirationDate
export const selectIsLoggedIn = state => state.login.loggedIn
export const selectIsLoading = state => state.login.isLoading
export const selectContract = state => state.login.contract
export const selectLanguage = state => state.login.language
export const selectUserNotificationsEnabled = state => state.login.notificationsEnabled
export const selectUserFavorites = state => state.login.user?.favorites || []
export const selectUserShortcuts = state => state.login.user?.shortcuts
export const selectUserId = state => state.login.user?.id
export const selectUser = state => state.login.user

export default loginSlice.reducer
